import { useCallback, useEffect, useRef } from "react"
import {
  useBeforeUnload,
  useBlocker,
  useNavigate,
  useParams,
  type BlockerFunction,
} from "react-router"
import { message } from "antd"
import { useShallow } from "zustand/react/shallow"
import { getRecreateTaskPath } from "~/common/router"
import { useHotVideoStore } from "~/store/hotVideo"
import { useDefaultVideoOptions } from "~/store/session"

const hasPendingScriptSave = () => {
  const { scriptSaveStatus } = useHotVideoStore.getState()
  return ["pending", "saving", "error"].includes(scriptSaveStatus)
}

export const useScriptSaveNavigation = (): void => {
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        currentLocation.pathname !== nextLocation.pathname &&
        hasPendingScriptSave(),
      [],
    ),
  )

  useBeforeUnload(
    useCallback((event) => {
      if (!hasPendingScriptSave()) return
      event.preventDefault()
      event.returnValue = ""
    }, []),
  )

  useEffect(() => {
    if (blocker.state !== "blocked") return
    let cancelled = false
    const saveBeforeLeaving = async (): Promise<void> => {
      const saved = await useHotVideoStore.getState().saveReplicaPrompt()
      if (cancelled) return
      if (!saved) {
        blocker.reset()
        void message.error("脚本尚未保存，请重试保存后再离开")
        return
      }
      // Include edits made while the preceding navigation save was running.
      if (hasPendingScriptSave()) return saveBeforeLeaving()
      blocker.proceed()
    }
    void saveBeforeLeaving()
    return () => {
      cancelled = true
    }
  }, [blocker])
}

export const useHotVideoTaskRoute = (): void => {
  useScriptSaveNavigation()
  const navigate = useNavigate()
  const { taskId } = useParams()
  const skipDraftRestoreTaskIdRef = useRef("")
  const { videoOptions } = useDefaultVideoOptions()
  const { clearTaskId, resetDraftFields, restoreTaskDraft, setVideoOptions } =
    useHotVideoStore(
      useShallow((state) => ({
        clearTaskId: state.clearTaskId,
        resetDraftFields: state.resetDraftFields,
        restoreTaskDraft: state.restoreTaskDraft,
        setVideoOptions: state.setVideoOptions,
      })),
    )

  const createdTaskId = useHotVideoStore((state) => state.createdTaskId)

  useEffect(() => {
    if (!createdTaskId) return
    // 新建任务只补全 URL，跳过随之触发的草稿恢复
    skipDraftRestoreTaskIdRef.current = createdTaskId
    navigate(getRecreateTaskPath(createdTaskId), { replace: true })
  }, [createdTaskId, navigate])

  useEffect(
    () => () => {
      clearTaskId()
      resetDraftFields()
    },
    [clearTaskId, resetDraftFields],
  )

  useEffect(() => {
    if (!taskId) {
      skipDraftRestoreTaskIdRef.current = ""
      clearTaskId()
      resetDraftFields()
      return
    }
    // 新建任务只补全 URL，不能打断当前生成请求或重新恢复草稿。
    if (skipDraftRestoreTaskIdRef.current === taskId) {
      skipDraftRestoreTaskIdRef.current = ""
      return
    }
    void restoreTaskDraft(taskId)
  }, [taskId, clearTaskId, resetDraftFields, restoreTaskDraft])

  useEffect(() => {
    if (!taskId) setVideoOptions(videoOptions, "auto")
  }, [taskId, videoOptions, setVideoOptions])
}
